import type { RecordValue } from "./types.ts";
import { sourceContribution } from "./resultContribution.ts";

export function resultNumber(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string" || String(value).trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function sourceIdentity(source: RecordValue): string {
  return JSON.stringify([source.roundId ?? "", source.channel ?? "ALL", source.roleCode ?? ""]);
}

export function resultCount(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

export type SnapshotCatalogue = { items: RecordValue[]; latestSnapshot?: RecordValue | null; publishedSnapshotId?: string | null };

/** The catalogue is ID-paginated; only server metadata can identify the global latest snapshot. */
export function snapshotOptions(catalogue: SnapshotCatalogue, firstPage: boolean): RecordValue[] {
  const items = [...catalogue.items];
  if (firstPage && catalogue.latestSnapshot?.snapshotId && !items.some(item => item.snapshotId === catalogue.latestSnapshot!.snapshotId)) {
    items.push(catalogue.latestSnapshot);
  }
  if (firstPage && catalogue.publishedSnapshotId && !items.some(item => item.snapshotId === catalogue.publishedSnapshotId)) {
    items.push({ snapshotId: catalogue.publishedSnapshotId });
  }
  return items.sort((a, b) => Number(b.createdAtEpochMillis || 0) - Number(a.createdAtEpochMillis || 0));
}

export function defaultSnapshot(catalogue: SnapshotCatalogue, firstPage: boolean): string {
  return String((firstPage ? catalogue.latestSnapshot?.snapshotId : null) || snapshotOptions(catalogue, firstPage)[0]?.snapshotId || "");
}

export function roundStatusObservation(data: RecordValue, roundId: string, minimumVersion: number) {
  if (data.roundId !== roundId || resultCount(data.version) === null || Number(data.version) < minimumVersion ||
      !["DRAFT", "READY", "OPEN", "CLOSED", "SEALED"].includes(String(data.state))) return null;
  return { state: String(data.state), version: Number(data.version) };
}

/** Snapshot values remain authoritative; never reconstruct rounded contributions or voter totals. */
export function resultMatrix(rows: RecordValue[]) {
  const sources = new Map<string, RecordValue>();
  for (const row of rows) for (const source of Array.isArray(row.sources) ? row.sources as RecordValue[] : []) {
    const key = sourceIdentity(source);
    if (!sources.has(key)) sources.set(key, source);
  }
  return {
    sources: [...sources].map(([key, source]) => ({ key, source })),
    rows: rows.map(row => ({ row, sources: new Map((Array.isArray(row.sources) ? row.sources as RecordValue[] : [])
      .map(source => [sourceIdentity(source), { source, contribution: sourceContribution(source) }])) })),
  };
}
